<template>
  <div class="working-shift-page">
    <block-buttons
      title="Рабочие смены" 
      description="Открытие, завершение и история работы" 
      :actions="toolbarActions"
    >
      <template #icon>
        <Clock3 :size="22" />
      </template>
    </block-buttons>

    <dl
      v-if="isDataLoaded"
      class="working-shift-page__summary"
    >
      <div>
        <dt>Текущая смена</dt>
        <dd>{{  getOpenedWorkingShift ? 'Открыта' : 'Закрыта' }}</dd>
      </div>
      <div>
        <dt>Длительность текущей смены</dt>
        <dd>{{ currentDuration }}</dd>
      </div>
      <div>
        <dt>Закрытых / всего смен</dt>
        <dd>{{ `${closedWorkingShifts.length} / ${workingShifts.length}` }}</dd>
      </div>
      <div>
        <dt>Средняя длительность закрытой смены</dt>
        <dd>{{ averageDuration }}</dd>
      </div>
    </dl>
    
    <p
      v-if="!isDataLoaded"
      class="working-shift-page__notice"
    >
      <template v-if="getOpenedWorkingShift">
        Смена № {{ getOpenedWorkingShift.id }} открыта {{ formatDate(getOpenedWorkingShift.openingAt) }}.
        После закрытия изменить данные смены будет нельзя.
      </template>

      <template v-else>
        Откройте смену, чтобы создавать продажи и операции по кассе.
      </template>

      Показатели рассчитаны по загруженной истории смен.
    </p>

    <data-table
      :rows="filteredRenderData"
      :columns="tableColumns"
      :loading="loading"
      :error="error"
      :search-keys="['userName', 'closedByName', 'openingAtFormatted', 'closingAtFormatted', 'statusName']"
      title="История рабочих смен"
      description="Сотрудники, время работы и статус смен"
      search-placeholder="Сотрудник или дата"
      empty-text="Рабочих смен с выбранным статусом нет"
      @retry="loadDataWrapper(true)"
    >
      <template #header>
        <span class="working-shift-page__badge">Смен: {{ filteredRenderData.length }}</span>
      </template>

      <template #filters>
        <label class="working-shift-page__filter">Статус
          <select v-model="status">
            <option value="">Все смены</option>
            <option value="open">Открытые</option>
            <option value="closed">Закрытые</option>
          </select>
        </label>
      </template>

      <template #cell-isOpened="{ row }">
        <span
          class="working-shift-page__badge"
          :class="{ 'working-shift-page__badge--closed': !row.isOpened }"
        >
          {{ row.statusName }}
        </span>
      </template>

      <template #cell-openingAt="{ row }">
        <strong>{{ row.openingAtFormatted }}</strong>
        <small>Смена № {{ row.id }}</small>
      </template>

      <template #cell-closingAt="{ row }">{{ row.closingAtFormatted }}</template>

      <template #cell-duration="{ row }">{{ row.duration }}</template>

      <template #cell-actions="{ row }">
        <select
          :disabled="loading"
          :aria-label="`Действия со сменой ${row.id}`"
          @change="selectAction($event, row)"
        >
          <option value="">Действия</option>

          <option
            v-for="action in rowActions"
            :key="action.id"
            :value="action.id"
          >
            {{ action.label }}
          </option>
        </select>
      </template>
    </data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { Clock3, Play, Square, RefreshCw } from 'lucide-vue'
import moment from 'moment'
import BlockButtons from '@/components/blockButtons'
import DataTable from '@/components/dataTable'
import permissions from '@/mixins/permissions'
import { formatDate } from '@/helper'

export default {
  name: 'WorkingShiftV2',
  components: { Clock3, BlockButtons, DataTable },
  mixins: [permissions],
  data () { return {
    loading: false,
    error: '',
    status: '',
    dateNow: moment().valueOf(),
    refreshAfterModal: false }
  },
  computed: {
    ...mapGetters('workingShift', {
      workingShifts: 'getData',
      getOpenedWorkingShift: 'getOpenedWorkingShift'
    }),
    ...mapGetters('settings', ['getTableColumns']),
    isDataLoaded () {
      return this.loading || Boolean(this.error)
    },
    closedWorkingShifts () {
      return this.workingShifts.filter(({ isOpened, closingAt }) => !isOpened && closingAt)
    },
    currentDuration () {
      return this.getOpenedWorkingShift ? this.duration(this.getOpenedWorkingShift) : '—'
    },
    averageDuration () {
      const durations = this.closedWorkingShifts.map(shift => this.durationMs(shift)).filter(value => value != null)

      return durations.length
        ? this.formatDuration(durations.reduce((sum, value) => sum + value, 0) / durations.length)
        : '—'
    },
    renderData () {
      return this.workingShifts.map(workingShift => ({
        ...workingShift,
        duration: this.duration(workingShift)
      }))
    },
    filteredRenderData () {
      return this.status
        ? this.renderData.filter(({ isOpened }) => this.status === 'open' ? isOpened: !isOpened)
        : this.renderData
    },
    tableColumns () {
      return [
        {
          key: 'actions',
          label: 'Действия',
          sortable: false
        },
        ...(this.getTableColumns?.['working-shift'] || [])
      ]
    },
    toolbarActions () {
      return [
        {
          id: 'refresh',
          label: 'Обновить',
          icon: RefreshCw,
          disabled: this.loading,
          handler: () => this.loadDataWrapper(true)
        },
        {
          id: 'toggle',
          label: this.getOpenedWorkingShift ? 'Закрыть смену' : 'Открыть смену',
          icon: this.getOpenedWorkingShift ? Square : Play,
          primary: true,
          disabled: this.isDataLoaded,
          handler: this.toggleWorkingShift
        }
      ]
    },
    rowActions () {
      return [
        {
          id: 'info',
          label: 'Информация',
          visible: true
        },
        {
          id: 'change',
          label: 'Редактировать',
          visible: this.isAllowedOperation('change', 'user')
        },
        {
          id: 'remove',
          label: 'Удалить',
          visible: this.isAllowedOperation('remove', 'user')
        }
      ].filter(({ visible }) => visible)
    }
  },
  mounted () {
    this.loadDataWrapper()

    this.clockTimer = setInterval(() => {
      this.dateNow = moment().valueOf()
    }, 60000)
  },
  beforeDestroy () { clearInterval(this.clockTimer) },
  methods: {
    ...mapActions('workingShift', ['loadData', 'open', 'close', 'remove']),
    ...mapActions('modal', ['openModal']),
    formatDate,
    durationMs ({
      openingAt = '',
      isOpened = '',
      closingAt = ''
    }) {
      if (!openingAt || (!isOpened && !closingAt)) {
        return null
      }

      const start = moment(openingAt)
      const end = moment(closingAt || this.dateNow)

      return start.isValid() && end.isValid() && !end.isBefore(start) ? end.diff(start) : null
    },
    formatDuration (ms) {
      if (ms == null) {
        return '—'
      }

      const minutes = Math.floor(ms / 60000)

      return `${Math.floor(minutes / 60)} ч ${minutes % 60} мин`
    },
    duration (workingShift) {
      return this.formatDuration(this.durationMs(workingShift))
    },
    async loadDataWrapper (force = false) {
      if (this.loading) {
        return
      }

      this.$bus.emit('start-loader')
      this.loading = true

      await this.loadData({ force })

      this.loading = false
      this.$bus.emit('stop-loader')
    },
    selectAction (event, row) {
      const action = event.target.value

      event.target.value = ''

      if (!action) {
        return
      }

      this.openAction(action, row)
    },
    openAction(action, row) {
      switch (action) {
        case 'info':
          this.openModal({
            name: 'info-base',
            propsData: {
              name: 'working-shift',
              moduleAction: 'workingShift',
              type: 'info',
              title: `Информация - Рабочая смена ${row.openingAtFormatted} - ${row.closingAtFormatted}`,
              textCancelBtn: 'Закрыть',
              data: row
            }
          })

          break
        case 'change':
          this.openModal({
            name: 'change-base',
            propsData: {
              name: 'working-shift',
              moduleAction: 'workingShift',
              type: 'change',
              title: `Редактирование - Рабочая смена ${row.openingAtFormatted} - ${row.closingAtFormatted}`,
              text: 'Измените необходимые данные',
              textCancelBtn: 'Закрыть',
              textConfirmBtn: 'Сохранить',
              data: row
            }
          })

          break
        case 'remove':
          this.removeWorkingShift(row)
      }
    },
    async removeWorkingShift ({ id }) {
      const { error } = await this.remove(id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при удалении!',
          text: 'Произошла ошибка при удалении рабочей смены!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.$notify({
        type: 'success',
        title: 'Рабочая смена успешно удалена!',
        text: '',
        clean: false,
        duration: 3000
      })
    },
    toggleWorkingShift () {
      const callback = this.getOpenedWorkingShift
        ? this.close
        : this.open

      const title = this.getOpenedWorkingShift
        ? 'Закрытие рабочей смены'
        : 'Открытие рабочей смены'

      const text = this.getOpenedWorkingShift
        ? 'Укажите сумму в кассе перед закрытием рабочей смены'
        : 'Укажите сумму в кассе перед открытием рабочей смены'

      const textConfirmBtn = this.getOpenedWorkingShift
        ? 'Закрыть рабочую смену'
        : 'Открыть рабочую смену'

      this.openModal({
        name: 'create-base',
        propsData: {
          name: 'working-shift-amount',
          moduleAction: '',
          callback,
          type: 'create',
          title,
          text,
          textCancelBtn: 'Отмена',
          textConfirmBtn,
        }
      })
    }
  }
}
</script>

<style scoped lang="scss" src="./style.scss" />
